import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Calendar, DollarSign, Activity, MapPin, Search, ToggleLeft, ToggleRight, Flame, Mail, Plus, Trash2, Settings, Save, TrendingUp, UserCheck, X as IconX, Check as IconCheck, Lock, QrCode } from 'lucide-react';
import ChangePasswordModal from '../components/ChangePasswordModal';
import TestDataCleanupModal from '../components/TestDataCleanupModal';
import ActivationFunnelWidget from '../components/ActivationFunnelWidget';
import WaitlistTab from '../components/WaitlistTab';
import CleanerReferralQRModal from '../components/CleanerReferralQRModal';
import Header from '../components/Header';
import CleanerMap from '../components/CleanerMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [surgeZones, setSurgeZones] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [zipRates, setZipRates] = useState([]);
  const [newZip, setNewZip] = useState('');
  const [newRate, setNewRate] = useState('');
  const [platformSettings, setPlatformSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [sources, setSources] = useState(null);
  const [pendingCleaners, setPendingCleaners] = useState([]);
  const [approvalActionId, setApprovalActionId] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [qrCleaner, setQrCleaner] = useState(null); // { id, name } | null

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, usersRes, cleanersRes, bookingsRes, transactionsRes, surgeRes, contactRes, ratesRes, settingsRes, sourcesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/cleaners`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/transactions`, { headers }),
        axios.get(`${API}/admin/surge-zones`, { headers }),
        axios.get(`${API}/admin/contact-messages`, { headers }),
        axios.get(`${API}/admin/zip-rates`, { headers }),
        axios.get(`${API}/admin/platform-settings`, { headers }),
        axios.get(`${API}/admin/signup-sources`, { headers }),
        axios.get(`${API}/admin/cleaners/pending`, { headers }),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCleaners(cleanersRes.data);
      setBookings(bookingsRes.data);
      setTransactions(transactionsRes.data);
      setSurgeZones(surgeRes.data);
      setContactMessages(contactRes.data);
      setZipRates(ratesRes.data);
      setPlatformSettings(settingsRes.data);
      setSettingsDraft(settingsRes.data);
      setSources(sourcesRes.data);
      setPendingCleaners(pendingRes.data?.pending || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCleanerActive = async (cleanerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/cleaners/${cleanerId}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const saveZipRate = async (zip, rate) => {
    if (!zip || !rate) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/zip-rates/${zip}`,
        { base_rate: parseFloat(rate) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewZip(''); setNewRate('');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save rate');
    }
  };

  const deleteZipRate = async (zip) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/zip-rates/${zip}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to delete rate:', error);
    }
  };

  const savePlatformSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/platform-settings`, settingsDraft, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert('Platform settings updated');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} setUser={setUser} />
        <div className="flex items-center justify-center py-24">
          <p className="text-text-secondary">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const approveCleaner = async (cleanerId) => {
    setApprovalActionId(cleanerId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/cleaners/${cleanerId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingCleaners(prev => prev.filter(c => c.id !== cleanerId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setApprovalActionId(null);
    }
  };

  const rejectCleaner = async (cleanerId) => {
    const reason = window.prompt('Optional rejection reason (visible in email to cleaner):') || '';
    if (reason === null) return;
    setApprovalActionId(cleanerId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/cleaners/${cleanerId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingCleaners(prev => prev.filter(c => c.id !== cleanerId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setApprovalActionId(null);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={setUser} />
      
      <div className="px-6 md:px-12 lg:px-16 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-text-primary mb-2" data-testid="admin-dashboard">
              Admin Dashboard
            </h1>
            <p className="text-text-secondary">Manage users, bookings, and platform analytics</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-border overflow-x-auto">
            {['overview', 'settings', 'rates', 'surge', 'map', 'coverage', 'sources', 'waitlist', 'approvals', 'users', 'cleaners', 'bookings', 'transactions', 'messages'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 px-4 font-medium transition-all duration-300 whitespace-nowrap capitalize ${
                  activeTab === tab
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-text-secondary hover:text-primary'
                }`}
                data-testid={`${tab}-tab`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Overview */}
          {activeTab === 'overview' && stats && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Total Users</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">{stats.total_users}</p>
                  <p className="text-xs text-text-secondary mt-1">
                    {stats.total_customers} customers • {stats.total_cleaners} cleaners
                  </p>
                </div>

                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Bookings</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">{stats.total_bookings}</p>
                  <p className="text-xs text-text-secondary mt-1">
                    {stats.confirmed_bookings} confirmed • {stats.completed_bookings} completed
                  </p>
                </div>

                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <DollarSign className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Revenue</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">${stats.total_revenue.toFixed(2)}</p>
                  <p className="text-xs text-text-secondary mt-1">{stats.total_transactions} transactions</p>
                </div>

                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <Activity className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Active Cleaners</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">
                    {cleaners.filter(c => c.is_active).length}
                  </p>
                  <p className="text-xs text-text-secondary mt-1">of {cleaners.length} total</p>
                </div>
              </div>

              {/* Activation funnel — only meaningful when waiver is enabled */}
              <ActivationFunnelWidget />

              <div className="bg-surface rounded-2xl p-6 border border-border">
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-4">Recent Bookings</h3>
                <div className="space-y-3">
                  {bookings.slice(0, 5).map(b => (
                    <div key={b.id} className="flex items-center justify-between p-4 bg-surface-muted rounded-xl">
                      <div>
                        <p className="font-semibold text-text-primary">{b.customer_name} → {b.cleaner_name}</p>
                        <p className="text-sm text-text-secondary">{b.service_date} • {b.service_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">${b.total_amount.toFixed(2)}</p>
                        <span className="text-xs text-text-secondary capitalize">{b.status.replace('_', ' ')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Platform Settings */}
          {activeTab === 'settings' && settingsDraft && (
            <div className="space-y-6">
              {/* Account Security */}
              <div className="bg-surface rounded-2xl p-6 border border-border" data-testid="account-security-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-text-primary flex items-center gap-2">
                      <Lock className="w-5 h-5 text-primary" />
                      Account Security
                    </h3>
                    <p className="text-sm text-text-secondary mt-1">
                      You&apos;re signed in as <strong>{user?.email}</strong>. Change your password regularly to keep the platform safe.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(true)}
                    className="btn-primary text-sm flex-shrink-0"
                    data-testid="change-password-button"
                  >
                    Change password
                  </button>
                </div>
              </div>

              <div className="bg-surface rounded-2xl p-6 border border-border">
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-text-primary flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Platform Settings
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  Control how the marketplace splits revenue and handles cancellations. All booking calculations use these values.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Platform Fee */}
                <div className="bg-surface-muted rounded-2xl p-5">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Platform Fee (%)
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Commission taken from each successful booking. The cleaner keeps the rest.
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={settingsDraft.platform_fee_percent}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, platform_fee_percent: parseFloat(e.target.value) })}
                    className="input-field w-full"
                    data-testid="platform-fee-input"
                  />
                  <p className="text-xs text-text-secondary mt-2">
                    On a $100 booking: <span className="font-semibold text-primary">platform earns ${(100 * settingsDraft.platform_fee_percent / 100).toFixed(2)}</span>, cleaner keeps ${(100 - 100 * settingsDraft.platform_fee_percent / 100).toFixed(2)}
                  </p>
                </div>

                {/* Cancellation Window */}
                <div className="bg-surface-muted rounded-2xl p-5">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Free Cancellation Window (hours)
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Cancellations made earlier than this are fully refunded.
                  </p>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settingsDraft.cancellation_window_hours}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, cancellation_window_hours: parseInt(e.target.value) })}
                    className="input-field w-full"
                    data-testid="cancel-window-input"
                  />
                </div>

                {/* Pricing Mode */}
                <div className="bg-surface-muted rounded-2xl p-5">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Bookings Open?
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Master switch. Turn <strong>off</strong> while recruiting cleaners — customers see a &ldquo;launching soon&rdquo; banner and can&apos;t book. Cleaners can still sign up + onboard payouts.
                  </p>
                  <div className="flex gap-2" data-testid="bookings-enabled-toggle">
                    {[
                      { val: true, label: 'Open' },
                      { val: false, label: 'Closed (pre-launch)' },
                    ].map(opt => {
                      const active = (settingsDraft.bookings_enabled ?? true) === opt.val;
                      return (
                        <button
                          key={String(opt.val)}
                          type="button"
                          onClick={() => setSettingsDraft({ ...settingsDraft, bookings_enabled: opt.val })}
                          className={`flex-1 px-4 py-2.5 rounded-full text-sm font-semibold transition ${
                            active
                              ? (opt.val ? 'bg-green-700 text-white shadow' : 'bg-amber-600 text-white shadow')
                              : 'bg-surface text-text-secondary border border-border hover:border-primary'
                          }`}
                          data-testid={`bookings-enabled-${opt.val ? 'on' : 'off'}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Pricing Mode */}
                <div className="bg-surface-muted rounded-2xl p-5">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Pricing Mode
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    <strong>Fixed:</strong> zip code rate is the price (admin-controlled). <strong>Floor:</strong> zip rate is a minimum; cleaners can charge above.
                  </p>
                  <div className="flex gap-2" data-testid="pricing-mode-toggle">
                    {['fixed', 'floor'].map(m => {
                      const active = (settingsDraft.pricing_mode || 'fixed') === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setSettingsDraft({ ...settingsDraft, pricing_mode: m })}
                          className={`flex-1 px-4 py-2.5 rounded-full text-sm font-semibold transition ${
                            active ? 'bg-primary text-white shadow' : 'bg-surface text-text-secondary border border-border hover:border-primary'
                          }`}
                          data-testid={`pricing-mode-${m}`}
                        >
                          {m === 'fixed' ? 'Fixed rate' : 'Price floor'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Background Check Payer */}
                <div className="bg-surface-muted rounded-2xl p-5 md:col-span-2" data-testid="bg-check-payer-card">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Background Check Cost — Who Pays?
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Background checks run ~$15–$30 per cleaner. Pick the policy that fits your stage.
                    <strong> Recommendation:</strong> <em>Platform pays + recoups</em> for launch (zero signup friction, near-zero net cost), flip to <em>Cleaner pays</em> once supply is healthy.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="bg-check-payer-toggle">
                    {[
                      { val: 'platform', label: 'Platform pays', sub: 'Absorbed as marketing cost' },
                      { val: 'platform_recoup', label: 'Platform pays + recoups', sub: 'Auto-deduct from first payout' },
                      { val: 'cleaner', label: 'Cleaner pays', sub: 'Charged at signup' },
                    ].map(opt => {
                      const active = (settingsDraft.bg_check_payer || 'platform_recoup') === opt.val;
                      return (
                        <button
                          key={opt.val}
                          type="button"
                          onClick={() => setSettingsDraft({ ...settingsDraft, bg_check_payer: opt.val })}
                          className={`text-left px-4 py-3 rounded-xl text-sm font-semibold transition ${
                            active
                              ? 'bg-primary text-white shadow'
                              : 'bg-surface text-text-secondary border border-border hover:border-primary'
                          }`}
                          data-testid={`bg-check-payer-${opt.val}`}
                        >
                          <div>{opt.label}</div>
                          <div className={`text-xs font-normal mt-0.5 ${active ? 'text-white/80' : 'text-text-secondary'}`}>
                            {opt.sub}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <label className="text-sm font-medium text-text-primary whitespace-nowrap">Fee amount ($):</label>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      step="0.01"
                      value={settingsDraft.bg_check_fee ?? 29.99}
                      onChange={(e) => setSettingsDraft({ ...settingsDraft, bg_check_fee: parseFloat(e.target.value) })}
                      className="input-field w-32"
                      data-testid="bg-check-fee-input"
                    />
                    <p className="text-xs text-text-secondary">
                      {(settingsDraft.bg_check_payer || 'platform_recoup') === 'platform'
                        ? 'Platform absorbs this per cleaner.'
                        : (settingsDraft.bg_check_payer === 'cleaner')
                          ? 'Cleaner pays this at signup before BG check is sent.'
                          : 'Platform fronts this, auto-deducted from cleaner\u2019s first payout.'}
                    </p>
                  </div>

                  {/* Recoupment waiver — only relevant when "platform_recoup" */}
                  {(settingsDraft.bg_check_payer || 'platform_recoup') === 'platform_recoup' && (
                    <div className="mt-5 pt-5 border-t border-border" data-testid="bg-check-waiver-card">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settingsDraft.bg_check_recoup_waive_enabled ?? true}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, bg_check_recoup_waive_enabled: e.target.checked })}
                          className="mt-1"
                          data-testid="bg-check-waiver-enabled"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-text-primary">
                            Reward early activators: waive recoupment for go-getters
                          </p>
                          <p className="text-xs text-text-secondary mt-1">
                            If a cleaner completes N bookings within D days of signup, we
                            <strong> skip the recoupment</strong> as an activation reward. Pay only
                            for cleaners who ghost — the best ones earn free onboarding. Great for
                            your recruitment flyer.
                          </p>
                        </div>
                      </label>
                      {(settingsDraft.bg_check_recoup_waive_enabled ?? true) && (
                        <div className="mt-3 ml-7 flex flex-wrap items-center gap-3">
                          <span className="text-sm text-text-secondary">Complete</span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            value={settingsDraft.bg_check_recoup_waive_bookings ?? 5}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, bg_check_recoup_waive_bookings: parseInt(e.target.value) })}
                            className="input-field w-20"
                            data-testid="bg-check-waiver-bookings"
                          />
                          <span className="text-sm text-text-secondary">bookings within</span>
                          <input
                            type="number"
                            min="1"
                            max="365"
                            step="1"
                            value={settingsDraft.bg_check_recoup_waive_days ?? 30}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, bg_check_recoup_waive_days: parseInt(e.target.value) })}
                            className="input-field w-20"
                            data-testid="bg-check-waiver-days"
                          />
                          <span className="text-sm text-text-secondary">days of signup → waive the ${(settingsDraft.bg_check_fee ?? 29.99).toFixed(2)} deduction.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Cancellation Fee */}
                <div className="bg-surface-muted rounded-2xl p-5">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Late Cancellation Fee (%)
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Percentage of booking total kept when customer cancels within the window.
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={settingsDraft.cancellation_fee_percent}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, cancellation_fee_percent: parseFloat(e.target.value) })}
                    className="input-field w-full"
                    data-testid="cancel-fee-input"
                  />
                </div>

                {/* Cleaner Share */}
                <div className="bg-surface-muted rounded-2xl p-5">
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Cleaner Share of Cancel Fee (%)
                  </label>
                  <p className="text-xs text-text-secondary mb-3">
                    Of the cancellation fee, how much compensation goes to the cleaner.
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={settingsDraft.cancellation_cleaner_share_percent}
                    onChange={(e) => setSettingsDraft({ ...settingsDraft, cancellation_cleaner_share_percent: parseFloat(e.target.value) })}
                    className="input-field w-full"
                    data-testid="cancel-cleaner-share-input"
                  />
                </div>
              </div>

              {/* Example */}
              <div className="bg-primary-light/40 border border-primary/20 rounded-2xl p-5 mb-6">
                <h4 className="font-heading font-semibold text-text-primary mb-3">Example: $100 booking cancelled inside the window</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-white rounded-xl p-3 text-center">
                    <p className="text-xs text-text-secondary">Customer refund</p>
                    <p className="font-heading font-bold text-text-primary">${(100 - 100 * settingsDraft.cancellation_fee_percent / 100).toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 text-center">
                    <p className="text-xs text-text-secondary">Cleaner gets</p>
                    <p className="font-heading font-bold text-primary">${(100 * settingsDraft.cancellation_fee_percent / 100 * settingsDraft.cancellation_cleaner_share_percent / 100).toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 text-center">
                    <p className="text-xs text-text-secondary">Platform earns</p>
                    <p className="font-heading font-bold text-primary">${(100 * settingsDraft.cancellation_fee_percent / 100 * (100 - settingsDraft.cancellation_cleaner_share_percent) / 100).toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Cleaner Tier Settings */}
              <div className="border-t border-border pt-8 mb-6">
                <h4 className="font-heading text-lg font-semibold text-text-primary mb-2">Cleaner Tier Multipliers</h4>
                <p className="text-sm text-text-secondary mb-5">
                  Highly-rated cleaners earn a tier badge that increases their effective hourly rate — like Uber Black for cleaning.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Premium */}
                  <div className="bg-surface-muted rounded-2xl p-5 border-l-4" style={{ borderLeftColor: '#DDA77B' }}>
                    <h5 className="font-heading font-bold text-text-primary mb-3 flex items-center gap-2">
                      <span className="inline-block px-2 py-0.5 text-white text-xs font-bold rounded-full" style={{ backgroundColor: '#DDA77B' }}>PREMIUM</span>
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Min Rating (★)</label>
                        <input type="number" min="0" max="5" step="0.1"
                          value={settingsDraft.tier_premium_min_rating}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, tier_premium_min_rating: parseFloat(e.target.value) })}
                          className="input-field w-full" data-testid="tier-premium-rating" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Min Reviews</label>
                        <input type="number" min="0"
                          value={settingsDraft.tier_premium_min_reviews}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, tier_premium_min_reviews: parseInt(e.target.value) })}
                          className="input-field w-full" data-testid="tier-premium-reviews" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Multiplier (e.g. 1.15)</label>
                        <input type="number" min="1" step="0.05"
                          value={settingsDraft.tier_premium_multiplier}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, tier_premium_multiplier: parseFloat(e.target.value) })}
                          className="input-field w-full" data-testid="tier-premium-multiplier" />
                      </div>
                    </div>
                  </div>
                  {/* Elite */}
                  <div className="bg-surface-muted rounded-2xl p-5 border-l-4" style={{ borderLeftColor: '#7B61FF' }}>
                    <h5 className="font-heading font-bold text-text-primary mb-3 flex items-center gap-2">
                      <span className="inline-block px-2 py-0.5 text-white text-xs font-bold rounded-full" style={{ backgroundColor: '#7B61FF' }}>ELITE</span>
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Min Rating (★)</label>
                        <input type="number" min="0" max="5" step="0.1"
                          value={settingsDraft.tier_elite_min_rating}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, tier_elite_min_rating: parseFloat(e.target.value) })}
                          className="input-field w-full" data-testid="tier-elite-rating" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Min Reviews</label>
                        <input type="number" min="0"
                          value={settingsDraft.tier_elite_min_reviews}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, tier_elite_min_reviews: parseInt(e.target.value) })}
                          className="input-field w-full" data-testid="tier-elite-reviews" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Multiplier (e.g. 1.25)</label>
                        <input type="number" min="1" step="0.05"
                          value={settingsDraft.tier_elite_multiplier}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, tier_elite_multiplier: parseFloat(e.target.value) })}
                          className="input-field w-full" data-testid="tier-elite-multiplier" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time-of-day peak pricing */}
              <div className="bg-surface-muted rounded-xl p-5">
                <h4 className="font-heading text-base font-semibold text-text-primary mb-1">Time-of-Day Peak Pricing</h4>
                <p className="text-xs text-text-secondary mb-4">
                  Charge a premium during morning / evening rush hours. Multiplier <strong>1.0</strong> disables a window.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Morning */}
                  <div className="bg-surface rounded-lg p-4 border border-border">
                    <p className="text-sm font-semibold text-text-primary mb-3">Morning Peak</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Start</label>
                        <input type="time"
                          value={settingsDraft.peak_morning_start || '06:00'}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, peak_morning_start: e.target.value })}
                          className="input-field w-full text-sm" data-testid="peak-morning-start" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">End</label>
                        <input type="time"
                          value={settingsDraft.peak_morning_end || '09:00'}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, peak_morning_end: e.target.value })}
                          className="input-field w-full text-sm" data-testid="peak-morning-end" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Multiplier</label>
                        <input type="number" min="1" max="3" step="0.05"
                          value={settingsDraft.peak_morning_multiplier ?? 1.0}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, peak_morning_multiplier: parseFloat(e.target.value) })}
                          className="input-field w-full text-sm" data-testid="peak-morning-multiplier" />
                      </div>
                    </div>
                  </div>
                  {/* Evening */}
                  <div className="bg-surface rounded-lg p-4 border border-border">
                    <p className="text-sm font-semibold text-text-primary mb-3">Evening Peak</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Start</label>
                        <input type="time"
                          value={settingsDraft.peak_evening_start || '17:00'}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, peak_evening_start: e.target.value })}
                          className="input-field w-full text-sm" data-testid="peak-evening-start" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">End</label>
                        <input type="time"
                          value={settingsDraft.peak_evening_end || '20:00'}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, peak_evening_end: e.target.value })}
                          className="input-field w-full text-sm" data-testid="peak-evening-end" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Multiplier</label>
                        <input type="number" min="1" max="3" step="0.05"
                          value={settingsDraft.peak_evening_multiplier ?? 1.0}
                          onChange={(e) => setSettingsDraft({ ...settingsDraft, peak_evening_multiplier: parseFloat(e.target.value) })}
                          className="input-field w-full text-sm" data-testid="peak-evening-multiplier" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={savePlatformSettings}
                className="btn-primary flex items-center gap-2"
                data-testid="save-platform-settings"
              >
                <Save className="w-4 h-4" />
                Save Settings
              </button>
            </div>
            </div>
          )}

          {/* Zip Rates */}
          {activeTab === 'rates' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-text-primary flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  Zip Code Rate Floors
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  Set a minimum hourly rate per zip code. Any booking in that zip will charge at least this rate, even if the cleaner's rate is lower.
                </p>
              </div>

              {/* Add / Set rate form */}
              <div className="bg-surface-muted rounded-2xl p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-2">Zip Code</label>
                  <input
                    type="text"
                    value={newZip}
                    onChange={(e) => setNewZip(e.target.value)}
                    className="input-field w-full"
                    placeholder="10001"
                    data-testid="rate-zip-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-2">Base Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    className="input-field w-full"
                    placeholder="50.00"
                    data-testid="rate-amount-input"
                  />
                </div>
                <button
                  onClick={() => saveZipRate(newZip, newRate)}
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="save-zip-rate-button"
                >
                  <Plus className="w-4 h-4" />
                  Save Rate
                </button>
              </div>

              {/* Existing rates */}
              {zipRates.length === 0 ? (
                <p className="text-text-secondary text-center py-8">No zip rates set yet. Add one above — when <strong>Fixed</strong> mode is on, this is what customers pay per hour.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {zipRates.map(rate => {
                    const platformFee = (settingsDraft?.platform_fee_percent ?? 15) / 100;
                    const takeHome = rate.base_rate * (1 - platformFee);
                    const modeLabel = (settingsDraft?.pricing_mode || 'fixed') === 'fixed' ? '/hr customer rate' : '/hr floor';
                    return (
                      <div key={rate.zip_code} className="bg-surface-muted rounded-2xl p-5 border border-border" data-testid="zip-rate-card">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-xs text-text-secondary uppercase tracking-wide">Zip Code</p>
                            <p className="font-heading text-2xl font-bold text-text-primary">{rate.zip_code}</p>
                          </div>
                          <button
                            onClick={() => deleteZipRate(rate.zip_code)}
                            className="p-2 text-text-secondary hover:text-error transition-all"
                            data-testid="delete-zip-rate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <DollarSign className="w-5 h-5 text-primary" />
                          <span className="font-heading text-3xl font-bold text-primary">{rate.base_rate.toFixed(2)}</span>
                          <span className="text-sm text-text-secondary">{modeLabel}</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
                          <span className="text-text-secondary">Cleaner take-home</span>
                          <span className="font-semibold text-text-primary">${takeHome.toFixed(2)}/hr</span>
                        </div>
                        <p className="text-[10px] text-text-secondary mt-2 opacity-70">
                          Updated {new Date(rate.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Surge Zones */}
          {activeTab === 'surge' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-text-primary flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-600" />
                  Surge Zones (Live)
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  Demand-driven pricing across zip codes. Updated based on last 30 minutes of search activity.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {surgeZones.map(zone => (
                  <div
                    key={zone.zip_code}
                    className={`rounded-2xl p-5 border-2 ${
                      zone.level === 'peak' ? 'bg-red-50 border-red-300' :
                      zone.level === 'high' ? 'bg-orange-50 border-orange-300' :
                      zone.level === 'busy' ? 'bg-yellow-50 border-yellow-300' :
                      'bg-surface-muted border-border'
                    }`}
                    data-testid="surge-zone-card"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-text-secondary uppercase tracking-wide">Zip Code</p>
                        <p className="font-heading text-2xl font-bold text-text-primary">{zone.zip_code}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        zone.level === 'peak' ? 'bg-red-600 text-white' :
                        zone.level === 'high' ? 'bg-orange-600 text-white' :
                        zone.level === 'busy' ? 'bg-yellow-600 text-white' :
                        'bg-secondary text-white'
                      }`}>
                        {zone.label}
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-xs text-text-secondary">Multiplier</p>
                        <p className="font-heading text-3xl font-bold text-primary">{zone.multiplier.toFixed(2)}x</p>
                      </div>
                      <div className="text-right text-xs text-text-secondary">
                        <p>{zone.search_count} searches / 30m</p>
                        <p>{zone.active_cleaners} active cleaners</p>
                      </div>
                    </div>
                  </div>
                ))}
                {surgeZones.length === 0 && (
                  <p className="text-text-secondary col-span-3 text-center py-8">No zones to display.</p>
                )}
              </div>
            </div>
          )}

          {/* Map View */}
          {activeTab === 'map' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Live Cleaner Locations
              </h3>
              <p className="text-sm text-text-secondary mb-6">
                Real-time map showing all active cleaners on the platform.
              </p>
              <CleanerMap cleaners={cleaners.filter(c => c.latitude && c.longitude)} height="600px" />
            </div>
          )}

          {/* Signup Sources */}
          {activeTab === 'waitlist' && <WaitlistTab />}

          {/* Signup Sources */}
          {activeTab === 'sources' && (
            <div className="bg-surface rounded-2xl p-6 border border-border" data-testid="sources-panel">
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Where Sign-ups Come From
              </h3>
              <p className="text-sm text-text-secondary mb-6">
                Customers &amp; cleaners that landed via <code className="px-1.5 py-0.5 bg-surface-muted rounded text-xs">?ref=…</code>, <code className="px-1.5 py-0.5 bg-surface-muted rounded text-xs">?source=…</code>, or <code className="px-1.5 py-0.5 bg-surface-muted rounded text-xs">utm_*</code> on first visit. Add <code className="px-1.5 py-0.5 bg-surface-muted rounded text-xs">?ref=flyer</code> to any link to track that channel.
              </p>

              {sources && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="rounded-xl p-4 bg-primary-light/30 border border-primary/20">
                      <p className="text-xs uppercase tracking-wide text-text-secondary mb-1">Attributed sign-ups</p>
                      <p className="text-2xl font-heading font-bold text-primary" data-testid="sources-attributed">{sources.total_with_source}</p>
                    </div>
                    <div className="rounded-xl p-4 bg-surface-muted border border-border">
                      <p className="text-xs uppercase tracking-wide text-text-secondary mb-1">Unattributed</p>
                      <p className="text-2xl font-heading font-bold text-text-primary">{sources.unattributed}</p>
                    </div>
                    <div className="rounded-xl p-4 bg-surface-muted border border-border">
                      <p className="text-xs uppercase tracking-wide text-text-secondary mb-1">Total users</p>
                      <p className="text-2xl font-heading font-bold text-text-primary">{sources.total_users}</p>
                    </div>
                  </div>

                  {sources.sources.length === 0 ? (
                    <div className="text-center py-12 text-text-secondary">
                      <p className="text-sm">No tracked sign-ups yet.</p>
                      <p className="text-xs mt-2">Share a link like <code className="px-1.5 py-0.5 bg-surface-muted rounded">matchmycleaner.com/?ref=flyer</code> to start tracking.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="sources-table">
                        <thead>
                          <tr className="text-left text-text-secondary border-b border-border">
                            <th className="py-2 font-medium">Source</th>
                            <th className="py-2 font-medium text-center">Customers</th>
                            <th className="py-2 font-medium text-center">Cleaners</th>
                            <th className="py-2 font-medium text-center">Total</th>
                            <th className="py-2 font-medium text-right">Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sources.sources.map(s => {
                            const pct = sources.total_with_source > 0
                              ? Math.round((s.total / sources.total_with_source) * 100)
                              : 0;
                            return (
                              <tr key={s.source} className="border-b border-border/50 hover:bg-surface-muted/30">
                                <td className="py-3">
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-light/40 text-primary">
                                    {s.source}
                                  </span>
                                </td>
                                <td className="py-3 text-center text-text-primary">{s.customers}</td>
                                <td className="py-3 text-center text-text-primary">{s.cleaners}</td>
                                <td className="py-3 text-center font-semibold text-primary">{s.total}</td>
                                <td className="py-3 text-right">
                                  <div className="inline-flex items-center gap-2">
                                    <div className="w-16 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                                      <div className="h-full bg-primary" style={{ width: `${pct}%` }}></div>
                                    </div>
                                    <span className="text-xs text-text-secondary w-8 text-right">{pct}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Cleaner Approvals */}
          {activeTab === 'approvals' && (
            <div className="bg-surface rounded-2xl p-6 border border-border" data-testid="approvals-panel">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl font-semibold text-text-primary flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-primary" />
                  Pending Cleaner Approvals
                  {pendingCleaners.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold">
                      {pendingCleaners.length}
                    </span>
                  )}
                </h3>
              </div>
              <p className="text-sm text-text-secondary mb-6">
                Review new cleaner sign-ups before they become visible to customers. Approval triggers a welcome email and unlocks bookings.
              </p>

              {pendingCleaners.length === 0 ? (
                <div className="text-center py-16 text-text-secondary" data-testid="approvals-empty">
                  <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">All clear — no cleaners awaiting review.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {pendingCleaners.map(c => (
                    <div key={c.id} className="border border-border rounded-xl p-5 bg-surface-muted/40" data-testid={`pending-cleaner-${c.id}`}>
                      <div className="flex items-start gap-4 mb-3">
                        {c.profile_image ? (
                          <img
                            src={c.profile_image}
                            alt={c.name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-border flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-surface border-2 border-dashed border-amber-300 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold text-amber-700 text-center px-1">No photo</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-text-primary text-lg truncate">{c.name}</p>
                          <p className="text-xs text-text-secondary truncate">{c.email} · {c.phone}</p>
                          <p className="text-xs text-text-secondary mt-1">
                            Zip <strong className="text-primary">{c.zip_code}</strong> · ${c.hourly_rate}/hr · {c.years_experience} yrs exp
                          </p>
                          {c.signup_source && (
                            <span className="mt-2 inline-block text-xs px-2 py-0.5 rounded-full bg-primary-light/50 text-primary">
                              via {c.signup_source}
                            </span>
                          )}
                        </div>
                        <div className="text-right text-xs text-text-secondary flex-shrink-0 min-w-[110px]">
                          <p className="mb-1">BG check:</p>
                          <select
                            value={c.bg_check_status || 'pending_admin_review'}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(`${API}/admin/cleaners/${c.id}/bg-status`,
                                  { status: newStatus },
                                  { headers: { Authorization: `Bearer ${token}` } },
                                );
                                setPendingCleaners(prev => prev.map(x => x.id === c.id ? { ...x, bg_check_status: newStatus } : x));
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Could not update BG status');
                              }
                            }}
                            className="text-xs px-2 py-1 rounded border border-border bg-surface w-full"
                            data-testid={`bg-status-select-${c.id}`}
                          >
                            <option value="pending_admin_review">Pending review</option>
                            <option value="clear">Clear ✓</option>
                            <option value="consider">Consider</option>
                            <option value="suspended">Suspended</option>
                            <option value="not_started">Not started</option>
                          </select>
                        </div>
                      </div>
                      {c.bio && (
                        <p className="text-sm text-text-secondary mb-3 line-clamp-3 italic">&ldquo;{c.bio}&rdquo;</p>
                      )}
                      {Array.isArray(c.services) && c.services.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {c.services.slice(0, 6).map(s => (
                            <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-surface border border-border text-text-secondary">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => approveCleaner(c.id)}
                          disabled={approvalActionId === c.id}
                          className="btn-primary flex items-center gap-2 flex-1 justify-center text-sm py-2 disabled:opacity-60"
                          data-testid={`approve-${c.id}`}
                        >
                          <IconCheck className="w-4 h-4" /> Approve
                        </button>
                        <button
                          onClick={() => rejectCleaner(c.id)}
                          disabled={approvalActionId === c.id}
                          className="px-4 py-2 rounded-full border border-red-300 text-red-700 hover:bg-red-50 transition text-sm flex items-center gap-2 disabled:opacity-60"
                          data-testid={`reject-${c.id}`}
                        >
                          <IconX className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <h3 className="font-heading text-xl font-semibold text-text-primary">All Users</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setShowCleanupModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-700 hover:bg-red-100 text-sm font-medium border border-red-200 transition-colors"
                    data-testid="open-cleanup-modal"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove test users
                  </button>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="input-field pl-12"
                    />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Phone</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="border-b border-border hover:bg-surface-muted">
                        <td className="py-3 px-4 text-text-primary">{u.name}</td>
                        <td className="py-3 px-4 text-text-secondary">{u.email}</td>
                        <td className="py-3 px-4 text-text-secondary">{u.phone}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.role === 'admin' ? 'bg-accent text-white' :
                            u.role === 'cleaner' ? 'bg-primary text-white' :
                            'bg-secondary text-white'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-text-secondary text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cleaners */}
          {activeTab === 'cleaners' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-6">All Cleaners</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Zip</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Rate</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Experience</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Rating</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleaners.map(c => (
                      <tr key={c.id} className="border-b border-border hover:bg-surface-muted">
                        <td className="py-3 px-4 text-text-primary">{c.name}</td>
                        <td className="py-3 px-4 text-text-secondary">{c.zip_code}</td>
                        <td className="py-3 px-4 text-primary font-semibold">${c.hourly_rate}/hr</td>
                        <td className="py-3 px-4 text-text-secondary">{c.years_experience} yrs</td>
                        <td className="py-3 px-4 text-text-secondary">
                          {c.rating_avg > 0 ? `★ ${c.rating_avg.toFixed(1)} (${c.total_reviews})` : 'New'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            c.is_active ? 'bg-success text-white' : 'bg-error text-white'
                          }`}>
                            {c.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setQrCleaner({ id: c.user_id || c.id, name: c.name })}
                              className="text-primary hover:text-primary-hover"
                              title="Show referral QR"
                              data-testid="cleaner-qr-btn"
                            >
                              <QrCode className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => toggleCleanerActive(c.id)}
                              className="text-primary hover:text-primary-hover"
                              data-testid="toggle-cleaner-status"
                            >
                              {c.is_active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bookings */}
          {activeTab === 'bookings' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-6">All Bookings</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Cleaner</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Date/Time</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Service</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map(b => (
                      <tr key={b.id} className="border-b border-border hover:bg-surface-muted">
                        <td className="py-3 px-4 text-text-primary">{b.customer_name}</td>
                        <td className="py-3 px-4 text-text-primary">{b.cleaner_name}</td>
                        <td className="py-3 px-4 text-text-secondary text-sm">{b.service_date} {b.service_time}</td>
                        <td className="py-3 px-4 text-text-secondary text-sm">{b.service_type}</td>
                        <td className="py-3 px-4 text-primary font-semibold">${b.total_amount.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <select
                            value={b.status}
                            onChange={(e) => updateBookingStatus(b.id, e.target.value)}
                            className="input-field text-xs py-1 px-2"
                            data-testid="booking-status-select"
                          >
                            <option value="pending_payment">Pending Payment</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Transactions */}
          {activeTab === 'transactions' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-6">Payment Transactions</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Session ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Booking</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id} className="border-b border-border hover:bg-surface-muted">
                        <td className="py-3 px-4 text-text-secondary text-xs font-mono">{t.session_id?.substring(0, 25)}...</td>
                        <td className="py-3 px-4 text-text-secondary text-xs font-mono">{t.booking_id?.substring(0, 8)}...</td>
                        <td className="py-3 px-4 text-primary font-semibold">${t.amount.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            t.payment_status === 'paid' ? 'bg-success text-white' :
                            t.payment_status === 'initiated' ? 'bg-accent text-white' :
                            'bg-gray-200 text-gray-700'
                          }`}>
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-text-secondary text-sm">
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Contact Messages */}
          {activeTab === 'messages' && (
            <div className="bg-surface rounded-2xl p-6 border border-border">
              <div className="flex items-center gap-2 mb-6">
                <Mail className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-xl font-semibold text-text-primary">Contact Messages</h3>
                <span className="ml-2 text-sm text-text-secondary">
                  ({contactMessages.filter(m => m.status === 'new').length} new)
                </span>
              </div>
              {contactMessages.length === 0 ? (
                <p className="text-text-secondary text-center py-8">No messages yet.</p>
              ) : (
                <div className="space-y-4">
                  {contactMessages.map(m => (
                    <div
                      key={m.id}
                      className={`p-5 rounded-xl border ${
                        m.status === 'new' ? 'border-primary bg-primary-light/20' : 'border-border bg-surface-muted'
                      }`}
                      data-testid="contact-message-item"
                    >
                      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <h4 className="font-semibold text-text-primary">{m.subject}</h4>
                          <p className="text-sm text-text-secondary">
                            From <span className="font-medium">{m.name}</span> ({m.email})
                          </p>
                          <p className="text-xs text-text-secondary mt-1">
                            {new Date(m.created_at).toLocaleString()}
                          </p>
                        </div>
                        <select
                          value={m.status}
                          onChange={async (e) => {
                            const token = localStorage.getItem('token');
                            await axios.put(
                              `${API}/admin/contact-messages/${m.id}/status`,
                              { status: e.target.value },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            fetchData();
                          }}
                          className="input-field text-xs py-1 px-2"
                          data-testid="message-status-select"
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                      <p className="text-text-primary whitespace-pre-wrap">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ChangePasswordModal open={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
      <TestDataCleanupModal
        open={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        onDone={fetchData}
      />
      {qrCleaner && (
        <CleanerReferralQRModal
          cleanerId={qrCleaner.id}
          cleanerName={qrCleaner.name}
          onClose={() => setQrCleaner(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
